import React, { useEffect, useState } from "react";

import DetailDialog from "./DetailDialog";
import { SpecList, StarRow, SupplierAvatar } from "./DetailBits";
import { ReviewsSkeleton } from "./DetailSkeletons";
import useSupplierDetail from "../useSupplierDetail";
import { useSupplierRating, useSupplierReviews } from "../useSupplierRatings";
import { experienceLabel, formatAddressLine } from "../detailMappers";

const formatDay = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SupplierDetailSheet = ({
  open,
  supplierId,
  supplierName,
  initialTab = "about",
  priceLabel,
  footer,
  onClose,
}) => {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const { detail } = useSupplierDetail({ supplierId, coordinates: null });
  const rating = useSupplierRating({ supplierId, enabled: open });
  const reviews = useSupplierReviews({ supplierId, enabled: open && tab === "reviews" });

  const name = detail?.name || supplierName || "Supplier";

  const contact = [detail?.phoneNumber, detail?.email].filter(Boolean);
  const address = formatAddressLine(detail?.address);

  const businessDetails = [
    { label: "Address", value: address },
    { label: "Phone", value: detail?.phoneNumber },
    { label: "Email", value: detail?.email },
    { label: "Experience", value: experienceLabel(detail?.createdAt) },
    {
      label: "Delivery Service",
      value: detail
        ? detail.deliveryRadiusMeters > 0
          ? `Within ${(detail.deliveryRadiusMeters / 1000).toFixed(1)} km`
          : "Not Available"
        : "",
    },
  ];

  return (
    <DetailDialog
      open={open}
      title={name}
      onClose={onClose}
      variant="sheet"
      className="sy-supplier-sheet"
      footer={
        footer ||
        (priceLabel ? (
          <div className="sy-sheet-price">
            <span>Price</span>
            <b>{priceLabel}</b>
          </div>
        ) : null)
      }
    >
      <div className="sy-sheet-identity">
        <SupplierAvatar name={name} logoUrl={detail?.logoUrl} size={48} />

        <div>
          <p className="sy-sheet-name">{name}</p>

          {rating.total ? (
            <p className="sy-sheet-rating">
              <StarRow rating={rating.average} />
              {rating.average.toFixed(1)} ({rating.total})
            </p>
          ) : (
            <p className="sy-sheet-rating sy-muted">No ratings yet</p>
          )}
        </div>
      </div>

      <div className="sy-sheet-tabs" role="tablist" aria-label="Supplier details">
        {[
          { id: "about", label: "About" },
          { id: "reviews", label: "Reviews" },
        ].map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            className={`sy-sheet-tab${tab === entry.id ? " is-active" : ""}`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === "about" ? (
        <div className="sy-sheet-section">
          <p className="sy-sheet-about">
            {detail?.about || "This supplier has not added a description yet."}
          </p>

          {!contact.length && !address && detail && (
            <p className="sy-muted">No contact details shared.</p>
          )}

          <SpecList items={businessDetails} />
        </div>
      ) : (
        <div className="sy-sheet-section">
          {reviews.isLoading ? (
            <ReviewsSkeleton />
          ) : reviews.reviews.length ? (
            <ul className="sy-review-list">
              {reviews.reviews.map((review) => (
                <li key={review.id}>
                  <p className="sy-review-head">
                    <b>{review.authorName}</b>
                    <span>{formatDay(review.createdAt)}</span>
                  </p>

                  <StarRow rating={review.rating} />

                  {review.review && <p className="sy-review-body">{review.review}</p>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="sy-muted">No reviews yet for this supplier.</p>
          )}
        </div>
      )}
    </DetailDialog>
  );
};

export default SupplierDetailSheet;
